import React, { useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
const DoubleArrowIcon =
  'https://assets.askhippo.co.za/Icons/common/DoubleArrow_White.svg';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import ScrollFadeWrapper from '../ScrollFadeWrapper/ScrollFadeWrapper';
import Image from '../Image/Image';
import { toSentenceCase } from '../../utils/casing';

const HippoGenericMobileComponent = ({
  route,
  title,
  buttonText,
  imageDirection = 'bottom', // 'top' | 'bottom'
  color = 'white', // 'white' | 'grey' | 'blue' | 'green'
  image = 'HippoTickle', // key into imageMap below
  buttonColor,
}) => {
  const navigate = useNavigate();
  const buttonRef = useRef(null);

  // Bounce animation that resolves when complete
  const gs_bounce = (target, strength = 0.85) => {
    if (!target) return Promise.resolve();
    return new Promise((resolve) => {
      const tl = gsap.timeline({
        onComplete: resolve,
        defaults: { transformOrigin: '50% 50%' },
      });
      tl.to(target, {
        scale: strength,
        duration: 0.15,
        ease: 'power3.inOut',
      }).to(target, {
        scale: 1,
        duration: 0.85,
        ease: 'elastic.out(1.75, 0.4)',
      });
    });
  };

  const handleClick = async () => {
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReduced) {
      await gs_bounce(buttonRef.current);
    }
    navigate(route);
    window.scrollTo(0, 0);
  };

  const isTop = imageDirection === 'top';

  const backgroundColor =
    color === 'grey'
      ? '#E3E6E8'
      : color === 'blue'
        ? '#162F3B'
        : color === 'green'
          ? '#C7CA00'
          : '#FFFFFF';
  const textColor = color === 'blue' ? '#FFFFFF' : '#162F3B';

  const BASE = 'https://assets.askhippo.co.za/banners/mobile/';

  const imageMap = {
    HippoArmsCrossed: BASE + 'HippoArmsCrossed.svg',
    HippoDontKnow: BASE + 'HippoDontKnow.svg',
    HippoEyeCoverOhNo: BASE + 'HippoEyeCoverOhNo.svg',
    HippoHandsDown: BASE + 'HippoHandsDown.svg',
    HippoTickle: BASE + 'HippoTickle.svg',
    HippoHandSide: BASE + 'HippoHandSide.svg',
    HippoHandSideTiltFace: BASE + 'HippoHandSideTiltFace.svg',
    HippoHandsUp: BASE + 'HippoHandsUp.svg',
    HippoHandUp: BASE + 'HippoHandUp.svg',
    HippoOhNo: BASE + 'HippoOhNo.svg',
    HippoPhew: BASE + 'HippoPhew.svg',
    HippoThink: BASE + 'HippoThink.svg',
    HippoThinkTilt: BASE + 'HippoThinkTilt.svg',
    HippoThisWay: BASE + 'HippoThisWay.svg',
    HippoGottcha: BASE + 'HippoGottcha.svg',
    HippoBannerChamp: BASE + 'HippoBannerChamp.svg',
    HippoFibre: BASE + 'HippoFibre.svg',
  };

  const SelectedImage = imageMap[image] || imageMap.HippoTickle;
  const titleText = typeof title === 'string' ? title : '';

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      {/* Card */}
      <ScrollFadeWrapper>
        <Box
          sx={{
            width: '307px',
            minHeight: '408px',
            backgroundColor,
            borderRadius: '24px',
            padding: '32px 16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            boxSizing: 'border-box',
            gap: '24px',
          }}
        >
          {/* Hippo Image — top */}
          {isTop && (
            <ScrollFadeWrapper delay={0.15} startingOpacity={0.5}>
              <Image
                src={SelectedImage}
                alt={title}
                width={260}
                height={260}
                style={{
                  width: '260px',
                  height: 'auto',
                  pointerEvents: 'none',
                  userSelect: 'none',
                  marginTop: '-130px',
                  marginBottom: '8px',
                }}
              />
            </ScrollFadeWrapper>
          )}

          {/* Title */}
          <Typography
            component={'h3'}
            sx={{
              fontFamily: 'Pally, Inter, sans-serif',
              fontWeight: 700,
              fontSize: '2.125rem',
              lineHeight: '2.125rem',
              letterSpacing: '-0.03em',
              color: textColor,
              textAlign: 'center',
              px: '8px',
              whiteSpace: 'pre-line',
            }}
          >
            {titleText}
          </Typography>

          {/* Button */}
          <Button
            component={'a'}
            ref={buttonRef}
            variant="contained"
            onClick={handleClick}
            disableElevation
            sx={{
              height: '48px',
              backgroundColor: buttonColor === 'green' ? '#C7CA00' : '#FF0074',
              color: buttonColor === 'green' ? '#162F3B' : '#FFFFFF',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '1.25rem',
              lineHeight: '1.5rem',
              letterSpacing: '-0.03em',
              borderRadius: '999px',
              padding: '12px 24px',
              textTransform: 'none',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '4px',
              willChange: 'transform',
              '&:hover': {
                backgroundColor:
                  buttonColor === 'green' ? '#C7CA00' : '#FF0074',
              },
            }}
          >
            <span style={{ textAlign: 'center' }}>
              {toSentenceCase(buttonText)}
            </span>
            <img
              src={DoubleArrowIcon}
              alt="Double arrow icon"
              width={20}
              height={20}
              style={{
                display: 'block',
                marginLeft: '0px',
                filter: buttonColor === 'green' ? 'brightness(0)' : 'none',
              }}
            />
          </Button>

          {/* Hippo Image — bottom */}
          {!isTop && (
            <ScrollFadeWrapper delay={0.15} startingOpacity={0.5}>
              <Image
                src={SelectedImage}
                alt={title}
                width={260}
                height={260}
                style={{
                  width: '260px',
                  height: 'auto',
                  pointerEvents: 'none',
                  userSelect: 'none',
                  marginTop: '8px',
                }}
              />
            </ScrollFadeWrapper>
          )}
        </Box>
      </ScrollFadeWrapper>
    </Box>
  );
};

export default HippoGenericMobileComponent;
